import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { registerFormStyles, createDynamicStyles } from '../styles/components/RegisterForm.styles';
import { storeSettings, storeUtils, getAvailableStores } from '../data/storeData';
import { PREFECTURES } from '../data/prefectures';
import { registerMember } from '../services/memberService.js';
import { getRegisterFormTexts, registerFormTextHelpers } from '../data/registerFormTexts.js';
import { useRegistrationFlow } from '../context/RegistrationFlowContext';
import SuccessModal from '../components/ui/RegisterSuccessModal';
import { useToast } from '../components/ui/ToastManager';
import useAddressSearch from '../hooks/useAddressSearch';
import PostalCodeInput from '../components/ui/PostalCodeInput';
import {
  validatePassword,
  validatePhoneNumber,
  validateHiragana,
  validatePrefecture,
  formatPostalCode,
  checkPasswordStrength,
  isPasswordValid,
  VALIDATION_MESSAGES
} from '../utils/formValidation';
import { useNavigationBlock } from '../hooks/useNavigationBlock';
import NavigationBlockModal from '../components/ui/NavigationBlockModal';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../config/routes';
import { removeCookie } from '../utils/cookieUtils';
import { Eye, EyeOff } from 'lucide-react';

/**
 * ご利用開始日の上限（登録日＝今日から1カ月後、当日含む）をYYYY-MM-DD形式で取得
 * バックエンドのLocalDate.plusMonths(1)に合わせ、月末日のオーバーフローはその月の末日へ丸める
 * @returns {string} 上限日（YYYY-MM-DD）
 */
const getMaxServiceStartDate = () => {
  const today = new Date();
  const day = today.getDate();
  const target = new Date(today.getFullYear(), today.getMonth() + 1, day);
  // 例: 1/31 + 1カ月 → 2/31 は存在せず3月へ繰り上がるため、対象月の末日に丸める
  if (target.getDate() !== day) {
    target.setDate(0);
  }
  return target.toLocaleDateString('sv-SE').split('T')[0];
};

/**
 * 会員登録フォームコンポーネント
 * ユーザーの会員登録情報を入力・バリデーション・送信するフォーム
 */
const RegisterForm = ({ language = 'ja' }) => {
  const [stores, setStores] = useState(['選択してください']);
  const [isLoadingStores, setIsLoadingStores] = useState(true);
  const navigate = useNavigate();
  const texts = getRegisterFormTexts(language);
  const { showError } = useToast();
  const { login } = useAuth();
  const { completeStep, completeStepWithoutAdvance, getVerifiedEmail, REGISTRATION_STEPS } = useRegistrationFlow();

  // ===== State管理 =====
  /**
   * フォームの全入力データを管理するメインstate
   * 会員登録に必要なすべてのフィールドの値を一元管理
   */
  const [formData, setFormData] = useState({
    shop: '', name: '', nameKana: '', email: '', phoneNumber: '', postalCode: '',
    prefecture: '', city: '', town: '', building: '', birthday: '',
    gender: texts.options.gender.male, desiredDate: '', emergencyName: '',
    emergencyPhoneNumber: '', password: '', passwordConfirm: '',
    paymentMethod: texts.options.paymentMethod.creditCard,
    shoeLocker: texts.options.shoeLocker.undecided,
    additionalShops: []
  });
  const [registrationPaymentMethod, setRegistrationPaymentMethod] = useState('');



  /**
   * フォーム送信処理中の状態管理state
   * 重複送信防止とUI状態制御（ボタン無効化等）に使用
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * 成功モーダルの表示制御state
   * 会員登録成功時に表示するモーダルの開閉状態
   */
  const [showSuccessModal, setShowSuccessModal] = useState(false);


  /**
   * パスワード確認フィールドの一致エラーメッセージstate
   * パスワードと確認用パスワードの不一致時にエラー表示
   */
  const [passwordError, setPasswordError] = useState('');

  /**
   * パスワード強度の各要件満足状況を管理するstate
   * 長さ、英大文字、英小文字、数字の各要件をリアルタイムでチェック
   */
  const [passwordStrength, setPasswordStrength] = useState({
    length: false, uppercase: false, lowercase: false, number: false
  });

  /**
   * ひらがな名前フィールドのバリデーションエラーメッセージstate
   * 不正文字（漢字、カタカナ、英数字）使用時にエラー表示
   */
  const [nameKanaError, setNameKanaError] = useState('');

  /**
   * 電話番号フィールドのバリデーションエラーメッセージstate
   * 不正な電話番号形式入力時にエラー表示
   */
  const [phoneNumberError, setPhoneNumberError] = useState('');

  /**
   * 登録成功時の会員IDを保存するstate
   * 成功モーダルや完了ページ遷移で使用
   */
  const [registeredMemberId, setRegisteredMemberId] = useState('');


  // 登録完了データを保持（ModalがCloseされたときに使用）
  const [pendingRegistrationData, setPendingRegistrationData] = useState(null);

  // パスワード表示/非表示切り替え用のstate
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  /**
   * ナビゲーションをブロックすべきかの判定
   * 送信中でなく、成功モーダルが表示されていない場合にブロック
   */
  const shouldBlockNavigation = !isSubmitting && !showSuccessModal && !pendingRegistrationData && !registeredMemberId;

  /**
   * ナビゲーションブロック用のフック
   * ページ離脱時に確認モーダルを表示する機能を提供
   */
  const { isModalOpen, handleConfirm, handleCancel } = useNavigationBlock(
    shouldBlockNavigation,
    null,
    ROUTES.TERMS
  );

  // コンポーネントマウント時に店舗リストを取得
  useEffect(() => {
    const loadStores = async () => {
      try {
        setIsLoadingStores(true);
        
        // 店舗リストを取得（IPチェック済み）
        const storeList = await getAvailableStores();
        setStores(storeList);
      } catch (error) {
        console.error('[REGISTER FORM] Failed to load stores:', error);
        showError('店舗リストの読み込みに失敗しました');
        // エラー時はデフォルトのリストを使用
        setStores(['選択してください']);
      } finally {
        setIsLoadingStores(false);
      }
    };

    loadStores();
  }, [showError]);

  // ===== 初期化処理 =====
  /**
   * コンポーネント初期化時の処理
   * RegistrationFlowContextから認証済みメールアドレスを取得し、入会希望日を今日に設定
   */
  useEffect(() => {
    // RegistrationFlowContextから認証済みメールアドレスを取得
    const verifiedEmail = getVerifiedEmail();

    // 今日の日付をYYYY-MM-DD形式で取得（入会希望日の初期値）
    const today = new Date().toLocaleDateString('sv-SE').split('T')[0];

    // フォームデータに初期値を設定
    setFormData(prev => ({
      ...prev,
      email: verifiedEmail || 'test@example.com',
      desiredDate: today
    }));

  }, [getVerifiedEmail]);

  // ===== パスワード一致チェック関数 =====
  /**
   * パスワードと確認用パスワードの一致チェック関数
   * リアルタイムでパスワード一致性をバリデーション
   */
  const validatePasswordMatch = (currentName, currentValue, formData) => {
    // パスワード関連フィールド以外は処理しない
    if (currentName !== 'passwordConfirm' && currentName !== 'password') return;

    // 変更されたフィールドに応じて最新の値を取得
    const password = currentName === 'password' ? currentValue : formData.password;
    const confirmPassword = currentName === 'passwordConfirm' ? currentValue : formData.passwordConfirm;

    // パスワードが不一致かつ確認用パスワードが空でない場合にエラー設定
    const hasPasswordMismatch = password !== confirmPassword && confirmPassword !== '';
    setPasswordError(hasPasswordMismatch ? VALIDATION_MESSAGES.PASSWORD_MISMATCH : '');
  };

  // ===== イベントハンドラー群 =====

  /**
   * 入力フィールドの値変更イベントハンドラー
   * すべての入力フィールドの変更を統一的に処理
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 郵便番号の場合は自動フォーマットを適用
    const formattedValue = name === 'postalCode' ? formatPostalCode(value) : value;

    // フォームデータを更新（不変性を保持）
    setFormData(prev => ({ ...prev, [name]: formattedValue }));

    // フィールド別リアルタイムバリデーション
    if (name === 'nameKana') {
      setNameKanaError(validateHiragana(formattedValue));
    }
    if (name === 'phoneNumber') {
      setPhoneNumberError(validatePhoneNumber(formattedValue));
    }
    if (name === 'password') {
      // 共通バリデーション関数を使用してパスワード強度をチェック
      setPasswordStrength(checkPasswordStrength(formattedValue));
    }

    // パスワード一致チェック
    validatePasswordMatch(name, formattedValue, formData);
  };

  // ✅ 複数店舗選択用のハンドラー（チェックボックス用）
  const handleAdditionalShopToggle = (shopName) => {
    setFormData(prev => {
      const currentShops = prev.additionalShops || [];
      const isSelected = currentShops.includes(shopName);
      
      if (isSelected) {
        // すでに選択されている場合は削除
        return { ...prev, additionalShops: currentShops.filter(shop => shop !== shopName) };
      } else {
        // 選択されていない場合は追加
        return { ...prev, additionalShops: [...currentShops, shopName] };
      }
    });
  };

  // パスワード表示/非表示切り替えハンドラー
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const togglePasswordConfirmVisibility = () => {
    setShowPasswordConfirm(prev => !prev);
  };

  const { postalMessage, isSuccess, searchAddress } = useAddressSearch(({ prefectureLabel, city, address }) => {
    setFormData(prev => ({ ...prev, prefecture: prefectureLabel, city, town: address }));
  });

  /**
   * フォーム送信前の最終バリデーション
   * @param {Object} formData - フォームデータ
   * @returns {string|null} エラーメッセージまたはnull
   */
  const validateSubmissionData = (formData) => {
    // パスワード一致チェック
    if (formData.password !== formData.passwordConfirm) {
      return VALIDATION_MESSAGES.PASSWORD_MISMATCH;
    }

    // パスワード強度チェック
    const passwordValidationError = validatePassword(formData.password);
    if (passwordValidationError) {
      return passwordValidationError;
    }

    // 都道府県バリデーション
    const prefectureValidationError = validatePrefecture(formData.prefecture);
    if (prefectureValidationError) {
      return prefectureValidationError;
    }

    // ご利用開始日バリデーション（登録日から1カ月以内）
    if (formData.desiredDate && formData.desiredDate > getMaxServiceStartDate()) {
      return VALIDATION_MESSAGES.SERVICE_START_DATE_TOO_FAR;
    }

    return null;
  };


  /**
   * 会員登録成功時の処理
   * @param {Object} result - API結果
   * @param {Object} formData - フォームデータ
   */
  const handleRegistrationSuccess = async (result, formData) => {

    const memberId = result.data?.memberId || '';
    setRegisteredMemberId(memberId);
    setRegistrationPaymentMethod(formData.paymentMethod);

    // 利用規約クッキーをクリア
    try {
      removeCookie('termsAgreementSession');
    } catch (error) {
      console.error('[REGISTER] ❌ クッキーのクリアに失敗:', error);
    }

    // バックエンドにログイン
    try {
      
      // AuthContextのloginメソッドを使用（内部でCSRF処理も行われる）
      await login({
        email: formData.email,
        password: formData.password
      });

      // 状態更新を確実にするため少し待つ
      await new Promise(resolve => setTimeout(resolve, 500));

    } catch (error) {
      console.error('[REGISTER] ❌ Login error:', error);
      showError(`ログイン処理に失敗しました: ${error.message}`, 8000);
      // ✅ エラー時は処理を中断
      return;
    }

    // 登録完了データを保持
    setPendingRegistrationData({
      memberInfo: formData,
      paymentMethod: formData.paymentMethod,
      memberId: memberId
    });

    // モーダルを表示
    setShowSuccessModal(true);
  };

  /**
   * 会員登録API呼び出しと結果処理（メイン処理）
   * @param {Object} formData - フォームデータ
   */
  const executeRegistration = async (formData) => {

    try {
      // バックエンドに会員登録を送信（Firebaseユーザー作成も含む）
      const registrationResult = await registerMember(formData);

      // 結果に応じて処理を分岐
      if (registrationResult.success) {
        await handleRegistrationSuccess(registrationResult, formData);
      } else {
        console.error('[REGISTER] ❌ Backend registration failed:', registrationResult.error);
        showError(`登録エラー: ${registrationResult.error}`, 8000);
      }

    } catch (error) {
      console.error('[REGISTER] ❌ Registration process failed:', error);
      showError(`登録処理でエラーが発生しました: ${error.message}`, 8000);
    }
  };

  /**
   * フォーム送信処理のメインハンドラー
   * 最終バリデーション → API送信 → 結果処理の流れで会員登録を実行
   */
  const handleSubmit = async (e) => {
    // デフォルトのフォーム送信動作を防止
    e.preventDefault();

    // 最終バリデーション
    const validationError = validateSubmissionData(formData);
    if (validationError) {
      return showError(validationError, 6000);
    }

    // 重複送信防止
    if (isSubmitting) return;

    // 送信中状態の設定
    setIsSubmitting(true);

    try {
      // 会員登録API呼び出し
      await executeRegistration(formData);
    } catch (error) {
      // 例外エラーハンドリング
      console.error('[REGISTER FORM] 会員登録処理エラー:', error);
      showError('システムエラーが発生しました。しばらく時間をおいてからお試しください。', 8000);
    } finally {
      // 送信中状態の解除
      setIsSubmitting(false);
    }
  };

  // ===== モーダル関連ハンドラー群 =====
  /**
    * 成功モーダルを閉じるハンドラー
    * 成功モーダルの×ボタンクリック時の処理
    */
  const handleCloseSuccessModal = (closeData) => {

    setShowSuccessModal(false);

    // shouldCompleteFlowがfalseの場合は何もしない（モーダルを×で閉じた場合）
    if (!closeData?.shouldCompleteFlow) {
      setPendingRegistrationData(null);
      return;
    }

    // データが存在することを確認
    if (!pendingRegistrationData) {
      console.warn('[REGISTER] No pending registration data');
      return;
    }

    // closeDataから直接isCreditCardを取得
    const isCreditCard = closeData.isCreditCard;

    if (isCreditCard) {
      // クレジットカードの場合
      completeStep(REGISTRATION_STEPS.MEMBER_REGISTRATION, {
        memberInfo: closeData.data.memberInfo,
        paymentMethod: closeData.data.paymentMethod,
        memberId: closeData.data.memberId
      });

      navigate(ROUTES.CREDIT_CARD_INPUT);
      setPendingRegistrationData(null);
      return;
    }

    // 口座振替の場合
    completeStepWithoutAdvance(REGISTRATION_STEPS.MEMBER_REGISTRATION, {
      memberInfo: closeData.data.memberInfo,
      paymentMethod: closeData.data.paymentMethod,
      memberId: closeData.data.memberId
    });

    navigate(`${ROUTES.REGISTRATION_COMPLETE}?memberId=${closeData.data.memberId}`, {
      state: {
        memberData: closeData.data.memberInfo,
        paymentMethod: closeData.data.paymentMethod,
        shouldCompleteFlow: true
      }
    });

    setPendingRegistrationData(null);
  };


  // ===== レンダリング用変数 =====
  const styles = registerFormStyles;
  const selectedStoreName = formData.shop;
  const hasShoeLocker = storeUtils.hasShoeLocker(selectedStoreName);

  // ===== フィールド定義（共通処理を関数化） =====
  /**
   * テキスト入力フィールドを生成する関数
   * 基本的な入力フィールドを統一的にレンダリング
   */
  const renderTextField = (name, label, required = false, props = {}) => (
    <tr key={name}>
      <th style={styles.th}>
        <span>{label}</span>{required && <span style={styles.required}> *</span>}
      </th>
      <td style={styles.td}>
        <input
          name={name}
          value={formData[name]}
          onChange={handleChange}
          style={styles.input}
          required={required}
          disabled={isSubmitting}
          {...props}
        />
      </td>
    </tr>
  );

  /**
   * セレクトボックスフィールドを生成する関数
   * ドロップダウン選択フィールドを統一的にレンダリング
   */
  const renderSelectField = (name, label, options, required = false, helpText) => (
    <tr key={name}>
      <th style={styles.th}>
        <span>{label}</span>{required && <span style={styles.required}> *</span>}
      </th>
      <td style={styles.td}>
        <select
          name={name}
          value={formData[name]}
          onChange={handleChange}
          style={styles.select}
          required={required}
          disabled={isSubmitting}
        >
          {options.map((option, index) => (
            <option key={`${option.value}-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {helpText && (
          <>
            <br />
            <span style={styles.smallText}>{helpText}</span>
          </>
        )}
      </td>
    </tr>
  );

  // チェックボックスグループ用のレンダリング関数
  const renderCheckboxGroup = (name, label, options, required = false, helpText) => (
    <tr key={name}>
      <th style={styles.th}>
        <span>{label}</span>{required && <span style={styles.required}> *</span>}
      </th>
      <td style={styles.td}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px',
          maxHeight: '200px',
          overflowY: 'auto',
          padding: '10px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          backgroundColor: '#f9f9f9'
        }}>
          {options.map((option) => (
            <label key={option.value} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '5px',
              cursor: 'pointer',
              backgroundColor: formData[name]?.includes(option.value) ? '#e3f2fd' : 'transparent',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}>
              <input
                type="checkbox"
                checked={formData[name]?.includes(option.value) || false}
                onChange={() => handleAdditionalShopToggle(option.value)}
                disabled={isSubmitting}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        {formData[name]?.length > 0 && (
          <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
            <strong>選択中: </strong>
            <span>{formData[name].join('、')}</span>
          </div>
        )}
        {helpText && (
          <>
            <br />
            <span style={styles.smallText}>{helpText}</span>
          </>
        )}
      </td>
    </tr>
  );

  /**
   * ラジオボタングループフィールドを生成する関数
   * 複数の選択肢から1つを選ぶラジオボタンを統一的にレンダリング
   */
  const renderRadioField = (name, label, options, required = false, helpText) => (
    <tr key={name}>
      <th style={styles.th}>
        <span>{label}</span>{required && <span style={styles.required}> *</span>}
      </th>
      <td style={styles.td}>
        <div style={styles.radioGroup}>
          {options.map((option) => (
            <label key={option.value} style={styles.radioLabel}>
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={formData[name] === option.value}
                onChange={handleChange}
                style={styles.radioInput}
                disabled={option.disabled || isSubmitting}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        {helpText && <div style={styles.smallText}>{helpText}</div>}
      </td>
    </tr>
  );

  // ===== パスワード強度インジケーター =====
  /**
   * パスワード強度インジケーターをレンダリングする関数
   * パスワード入力時に各要件の満足状況を視覚的に表示
   */
  const renderPasswordStrengthIndicator = () => {
    // パスワードが未入力の場合は何も表示しない
    if (formData.password.length === 0) return null;

    // パスワード要件の定義
    const requirements = [
      { key: 'length', label: '8文字以上', met: passwordStrength.length },
      { key: 'uppercase', label: '英大文字を含む (A-Z)', met: passwordStrength.uppercase },
      { key: 'lowercase', label: '英小文字を含む (a-z)', met: passwordStrength.lowercase },
      { key: 'number', label: '数字を含む (0-9)', met: passwordStrength.number }
    ];

    return (
      <div style={{ marginTop: '10px' }}>
        {/* 要件タイトル */}
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
          パスワード要件:
        </div>
        {/* 要件リスト */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {requirements.map(({ key, label, met }) => (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', fontSize: '13px',
              color: met ? '#28a745' : '#666'
            }}>
              {/* チェックマーク/バツマーク */}
              <span style={{
                marginRight: '8px', fontSize: '16px',
                color: met ? '#28a745' : '#dc3545'
              }}>
                {met ? '✓' : '✗'}
              </span>
              <span>{label}</span>
            </div>
          ))}
        </div>

        {/* 全要件満足時の成功メッセージ */}
        {isPasswordValid(passwordStrength) && (
          <div style={{
            marginTop: '8px', padding: '8px 12px',
            backgroundColor: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '4px', color: '#155724',
            fontSize: '13px', fontWeight: 'bold'
          }}>
            ✓ パスワード要件をすべて満たしています
          </div>
        )}
      </div>
    );
  };

  // パスワード入力コンテナのスタイル
  const passwordContainerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const passwordToggleStyle = {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s'
  };

  // ===== JSXレンダリング =====
  return (
    <>
      <div style={styles.container}>
        <form onSubmit={handleSubmit} noValidate>
          <div>{texts.form.requiredIndicator}</div>

          <table style={styles.table}>
            <tbody>
              {/* 店舗選択 */}
              <tr>
                <th style={styles.th}>
                  <span>{texts.labels.storeSelection}</span><span style={styles.required}> *</span>
                </th>
                <td style={styles.td}>
                  <select 
                    name="shop" 
                    value={formData.shop} 
                    onChange={handleChange}
                    style={styles.select}
                    required
                    disabled={isLoadingStores}
                  >
                    {isLoadingStores ? (
                      <option>読み込み中...</option>
                    ) : (
                      stores.map((store, index) => (
                        <option key={index} value={store}>
                          {store}
                        </option>
                      ))
                    )}
                  </select>
                </td>
              </tr>

              {/* 基本情報フィールド */}
              {renderTextField('name', texts.labels.name, true, {
                type: 'text',
                placeholder: texts.placeholders.name
              })}

              {/* 名前（ひらがな）- バリデーション付き */}
              <tr>
                <th style={styles.th}>
                  <span>{texts.labels.nameKana}</span><span style={styles.required}> *</span>
                </th>
                <td style={styles.td}>
                  <input
                    type="text"
                    name="nameKana"
                    value={formData.nameKana}
                    onChange={handleChange}
                    placeholder={texts.placeholders.nameKana}
                    style={createDynamicStyles.getInputStyle(!!nameKanaError)}
                    required
                    disabled={isSubmitting}
                  />
                  {nameKanaError && (
                    <div style={createDynamicStyles.getMessageStyle('error')}>
                      {nameKanaError}
                    </div>
                  )}
                  <div style={styles.smallText}>{texts.helpTexts.nameKanaNote}</div>
                </td>
              </tr>

              {/* メールアドレス - 読み取り専用 */}
              <tr>
                <th style={styles.th}>
                  <span>{texts.labels.email}</span><span style={styles.required}> *</span>
                </th>
                <td style={styles.td}>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={createDynamicStyles.getInputStyle(false, true)}
                    readOnly
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '0px', marginBottom: '0px' }}>
                    {texts.helpTexts.emailNote}
                  </div>
                </td>
              </tr>

              {/* 電話番号 - バリデーション付き */}
              <tr>
                <th style={styles.th}>
                  <span>{texts.labels.phoneNumber}</span><span style={styles.required}> *</span>
                </th>
                <td style={styles.td}>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder={texts.placeholders.phoneNumber}
                    style={createDynamicStyles.getInputStyle(!!phoneNumberError)}
                    required
                    disabled={isSubmitting}
                  />
                  {phoneNumberError && (
                    <div style={createDynamicStyles.getMessageStyle('error')}>
                      {phoneNumberError}
                    </div>
                  )}
                  <div style={styles.smallText}>{texts.helpTexts.phoneNumberNote}</div>
                </td>
              </tr>

              {/* 郵便番号 - 住所検索機能付き */}
              <PostalCodeInput
                label={texts.labels.postalCode}
                required
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                onSearch={searchAddress}
                postalMessage={postalMessage}
                isSuccess={isSuccess}
                disabled={isSubmitting}
                placeholder={texts.placeholders.postalCode}
                thStyle={styles.th}
                tdStyle={styles.td}
                inputStyle={{ ...styles.input, ...styles.postalInput }}
                requiredStyle={styles.required}
              />

              {/* 住所フィールド */}
              {renderSelectField('prefecture', texts.labels.prefecture, 
                PREFECTURES.map(pref => ({ value: pref.label, label: pref.label })),
                true
              )}
              {renderTextField('city', texts.labels.city, true)}
              {renderTextField('town', texts.labels.town, true)}
              {renderTextField('building', texts.labels.building, false)}

              {/* 生年月日・性別・入会希望日 */}
              {renderTextField('birthday', texts.labels.birthDate, true, { type: 'date' })}

              {renderRadioField('gender', texts.labels.gender, [
                { value: texts.options.gender.male, label: texts.options.gender.male },
                { value: texts.options.gender.female, label: texts.options.gender.female },
                { value: texts.options.gender.other, label: texts.options.gender.other }
              ], true)}

              {renderTextField('desiredDate', texts.labels.desiredDate, true, {
                type: 'date',
                min: new Date().toLocaleDateString('sv-SE').split('T')[0],
                max: getMaxServiceStartDate()
              })}

              {/* 緊急連絡先 */}
              {renderTextField('emergencyName', texts.labels.emergencyContactName, false, {
                placeholder: texts.placeholders.emergencyContactName
              })}

              {renderTextField('emergencyTel', texts.labels.emergencyContactPhone, false, {
                type: 'tel',
                placeholder: texts.placeholders.emergencyContactPhone
              })}

              {/* 支払い方法 */}
              {renderRadioField('paymentMethod', texts.labels.paymentMethod, [
                { value: texts.options.paymentMethod.creditCard, label: texts.options.paymentMethod.creditCard },
                { value: texts.options.paymentMethod.bankTransfer, label: texts.options.paymentMethod.bankTransfer }
              ], true, texts.helpTexts.paymentNote)}

              {/* 追加店舗・シューズロッカー */}
              {renderCheckboxGroup('additionalShops', texts.labels.additionalStore,
                stores.filter(store => store !== '選択してください' && store !== formData.shop).map(store => ({ value: store, label: store }))
                , false, texts.helpTexts.additionalStoreNote)}

              {renderRadioField('shoeLocker', texts.labels.shoeLocker, [
                {
                  value: texts.options.shoeLocker.apply,
                  label: texts.options.shoeLocker.apply,
                  disabled: !hasShoeLocker
                },
                { value: texts.options.shoeLocker.decline, label: texts.options.shoeLocker.decline },
                {
                  value: texts.options.shoeLocker.undecided,
                  label: texts.options.shoeLocker.undecided,
                  disabled: !hasShoeLocker
                }
              ], true, (
                <div>
                  <span>{texts.dynamic?.noShoeLockerStores ?
                    texts.dynamic.noShoeLockerStores(storeSettings.noShoeLockerStores) :
                    `※${storeSettings.noShoeLockerStores.join('、')}にはシューズロッカーがございません。`
                  }</span><br />
                  <span>{texts.helpTexts.shoeLockerNote}</span>
                  {!hasShoeLocker && selectedStoreName && (
                    <><br /><span style={{ color: '#ff0000', fontWeight: 'bold' }}>
                      {registerFormTextHelpers.createNoShoeLockerMessage(selectedStoreName, language)}
                    </span></>
                  )}
                </div>
              ))}

              {/* パスワード */}
              <tr>
                <th style={styles.th}>
                  <span>{texts.labels.password}</span><span style={styles.required}> *</span>
                </th>
                <td style={styles.td}>
                  <div style={passwordContainerStyle}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      minLength="8"
                      placeholder={texts.placeholders.password}
                      style={{
                        ...createDynamicStyles.getInputStyle(!isPasswordValid(passwordStrength) && formData.password.length > 0),
                        paddingRight: '45px'
                      }}
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      style={passwordToggleStyle}
                      disabled={isSubmitting}
                      aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {renderPasswordStrengthIndicator()}
                </td>
              </tr>

              {/* パスワード確認 */}
              <tr>
                <th style={styles.th}>
                  <span>{texts.labels.passwordConfirm}</span><span style={styles.required}> *</span>
                </th>
                <td style={styles.td}>
                  <div style={passwordContainerStyle}>
                    <input
                      type={showPasswordConfirm ? 'text' : 'password'}
                      name="passwordConfirm"
                      value={formData.passwordConfirm}
                      onChange={handleChange}
                      placeholder={texts.placeholders.passwordConfirm}
                      style={{
                        ...createDynamicStyles.getInputStyle(!!passwordError),
                        paddingRight: '45px'
                      }}
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordConfirmVisibility}
                      style={passwordToggleStyle}
                      disabled={isSubmitting}
                      aria-label={showPasswordConfirm ? 'パスワードを非表示' : 'パスワードを表示'}
                    >
                      {showPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {passwordError && (
                    <div style={createDynamicStyles.getMessageStyle('error')}>
                      {passwordError}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {/* 送信ボタン */}
          <div style={styles.centerAlign}>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                backgroundColor: isSubmitting ? '#ccc' : styles.submitButton.backgroundColor
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? texts.buttons.registering : texts.buttons.register}
            </button>
          </div>
        </form>
      </div>

      {/* 成功モーダル */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        memberId={registeredMemberId}
        paymentMethod={registrationPaymentMethod}
        memberData={formData}
        language={language}
      />

      <NavigationBlockModal
        isOpen={isModalOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default RegisterForm;